import { createApi } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from './baseQuery'
import { baseQuery, publicBaseQuery } from './baseQuery'
import { apiTags } from './apiTags'
import { ORGS_URL } from '../constants/endpoints'

// Types
export interface Organization {
  id: string
  name: string
  org_branding?: string
  primary_poc?: string
  secondary_poc?: string
  primary_phone?: string
  secondary_phone?: string
  created_at: string
  updated_at: string
}

export interface UpdateOrganizationReq {
  name?: string
  org_branding?: string
  primary_poc?: string
  secondary_poc?: string
  primary_phone?: string
  secondary_phone?: string
}

export interface UploadBrandingResp {
  message: string
  org_branding: string
}

// Public API slice for endpoints that don't require authentication
export const publicOrgsApi = createApi({
  reducerPath: 'publicOrgsApi',
  baseQuery: publicBaseQuery,
  tagTypes: [apiTags.Organization, apiTags.OrganizationBranding],
  endpoints: (builder) => ({
    getOrganizationPublic: builder.query<Organization, void>({
      query: () => '/org', // Use relative path since baseQuery already has /v1
      providesTags: [{ type: apiTags.Organization, id: 'CURRENT' }],
    }),

    getOrganizationBrandingPublic: builder.query<Blob, void>({
      query: () => ({
        url: '/org/branding', // Use relative path since baseQuery already has /v1
        responseHandler: async (response) => response.blob(),
      }),
      providesTags: [{ type: apiTags.OrganizationBranding, id: 'CURRENT' }],
    }),
  }),
})

// Authenticated API slice for endpoints that require authentication
export const orgsApi = createApi({
  reducerPath: 'orgsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Organization, apiTags.OrganizationBranding],
  endpoints: (builder) => ({
    updateOrganization: builder.mutation<Organization, UpdateOrganizationReq>({
      query: (data) => ({
        url: ORGS_URL,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [
        { type: apiTags.Organization, id: 'CURRENT' },
        { type: apiTags.OrganizationBranding, id: 'CURRENT' },
      ],
    }),

    uploadOrganizationBranding: builder.mutation<UploadBrandingResp, File>({
      query: (file) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `${ORGS_URL}/branding`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: [
        { type: apiTags.Organization, id: 'CURRENT' },
        { type: apiTags.OrganizationBranding, id: 'CURRENT' },
      ],
    }),
  }),
})

export const { useUpdateOrganizationMutation, useUploadOrganizationBrandingMutation } = orgsApi

export const { useGetOrganizationPublicQuery, useGetOrganizationBrandingPublicQuery } =
  publicOrgsApi
